import { EventEmitter, Injectable } from '@angular/core';
import { OrderItemAskForOrderIdError } from '@infrastructure/order-item-ask-for-order-id-error';
import { AddOrderItemExceptionModel } from '@models/add-order-item-exception-model';
import { AddOrderItemResultModel } from '@models/add-order-item-result-model';
import { OrderCreateModel } from '@models/api-models/order-create-model';
import { OrderItemsCreateRequestModel } from '@models/api-models/request-models/orderItemsCreateRequestModel';
import { StartkitMapping } from '@models/api-models/startkit-mapping.interface';
import { CustomerModel } from '@models/customer';
import { OrderItemModel } from '@models/order-item';
import { OrderItemMiscModel } from '@models/order-item-misc-model';
import { OrderIdsModel } from '@models/orderIds-model';
import { StartKitModel } from '@models/start-kit-model';
import { AppModeEnum, AppState } from '@shared/app-state';
import { Subject } from 'rxjs';
import { DataApiService } from './data-api.service';
import { EventsService } from './events.service';
import { OrderItemService } from './order-item.service';
import { OrderOnBehalfService } from './order-on-behalf.service';
import { OrdersDataService } from './orders-data.service';
import { UserService } from './user.service';
import { StartkitMappingService } from './v2/startkit-mapping.service';

@Injectable({
	providedIn: 'root'
})
export class OrderService {

	private addToBagSubject = new Subject();

	public orderItemUpdateCompleted: EventEmitter<boolean> = new EventEmitter();

	constructor(
		private dataApiService: DataApiService,
		public appState: AppState,
		private events: EventsService,
		private onBehalfService: OrderOnBehalfService,
		private userService: UserService,
		private orderDataService: OrdersDataService,
		private orderItemService: OrderItemService,
		private startkitMappingService: StartkitMappingService,
	) { }

	public addToBagObservable() {
		return this.addToBagSubject.asObservable();
	}

	public async AddMiscToBag(orderItemMiscModel: OrderItemMiscModel, newOrderSelected?: boolean): Promise<boolean> {
		orderItemMiscModel.CustomerNo = (await this.onBehalfService.getCustomerOrUserNo());

		return new Promise<boolean>(async (resolve, reject) => {

			try {
				if (orderItemMiscModel.OrderId !== undefined && (orderItemMiscModel.OrderId ?? 0) < 1) {
					const customer = this.onBehalfService.customerSnapshot();
					orderItemMiscModel.OrderId = await this.getOrCreateOrder(customer, newOrderSelected ?? false);
				}

				await this.orderDataService.createOrderMiscItem(orderItemMiscModel);
			} catch (error) {
				console.error('Error creating order item misc:', error);
				return reject(new AddOrderItemExceptionModel('Error creating order item misc:', this.orderItemService.GetMiscItemString(orderItemMiscModel)));
			}
			finally {
				this.events.showLoader(false);
			}
			resolve(true);
		});
	}

	public async UpdateStartkitMapping(orderItem: OrderItemModel, skm: StartKitModel, customerNo: string): Promise<void> {
		const startkitMapping: StartkitMapping = {
			CustomerNo: customerNo,
			ProductLineCode: skm.ProductLineCode,
			StartkitItemNo: skm.ItemNo,
		}

		this.startkitMappingService.postStartkitMapping(startkitMapping);
	}

	public async createOrderItemsByCollectionId(customerNo: string, collectionId: number, orderId: number, isDisplay: boolean,manualconfigSubsetIds: Number[]): Promise<void> {
		const requestModel = new OrderItemsCreateRequestModel(customerNo, collectionId, orderId, isDisplay, manualconfigSubsetIds);

		return new Promise(async (resolve, reject) => {

			try {
				await this.orderDataService.createOrderItemsByCollectionId(requestModel);
			} catch (error) {
				console.error('Error creating order item:', error);
				return reject(new AddOrderItemExceptionModel('Error adding frames from collection with id: ' + collectionId, 'Error adding frames from collection to bag.'));
			}

			resolve();
		});
	}

	public async AddFrameToOrder(
		orderItem: OrderItemModel,
		newOrderSelected?: boolean,
	): Promise<AddOrderItemResultModel> {
		orderItem.CustomerNo = (await this.onBehalfService.getCustomerOrUserNo());

		return new Promise<AddOrderItemResultModel>(async (resolve, reject) => {
			const customer = this.onBehalfService.customerSnapshot();
			const testCrmTicketId = this.appState.afterSalesCRMCaseNo.value;

			try {
				if (!orderItem.OrderId || orderItem.OrderId < 1) {
					// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
					// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
					// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
					// getOrCreateOrder takes an enum as param - we dont call it with an enum!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
					// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
					// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
					// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!

					orderItem.OrderId = await this.getOrCreateOrder(customer, newOrderSelected ?? false, testCrmTicketId);
				}
			}
			catch (error) {
				console.error('Error creating order item:', error);
				return reject(new AddOrderItemExceptionModel('Error creating order item:', this.orderItemService.GetVariantString(orderItem)));
			}


			try {
				await this.createOrderItem(orderItem);
			} catch (error) {
				console.error('Error creating order item:', error);
				return reject(new AddOrderItemExceptionModel('Error creating order item:', this.orderItemService.GetVariantString(orderItem)));
			}
			resolve(new AddOrderItemResultModel(false));
		});
	}

	private async getOrCreateOrder(customer: CustomerModel | null, newOrderSelected: boolean, crmTicketId?: string) {
		const customerOrUserNo = await this.onBehalfService.getCustomerOrUserNo();

		if (newOrderSelected) {
			return await this.createNewOrder(customer, customerOrUserNo, crmTicketId);
		}
		else {
			const ordersModel = (await this.dataApiService.getOrderIds(customerOrUserNo, crmTicketId));
			const orders = ordersModel?.filter(x => !x.IsLocked);

			if (orders && orders.length > 1) {
				throw new OrderItemAskForOrderIdError();
			}
			else if (orders && orders.length === 1) {
				return orders[0].Id;
			}
			else {
				return await this.createNewOrder(customer, customerOrUserNo, crmTicketId);
			}
		}
	}

	public async getOrderIds(): Promise<OrderIdsModel[]> {
		const customerOrUserNo = await this.onBehalfService.getCustomerOrUserNo();
		let orders = (await this.dataApiService.getOrderIds(customerOrUserNo, ''));
		const notLocked = orders?.filter(x => !x.IsLocked);

		return notLocked ?? [];
	}

	public async UpdateOrderIdsFromBackend(): Promise<boolean> {
		const customerOrUserNo = await this.onBehalfService.getCustomerOrUserNo();
		this.dataApiService.getOrderIds(customerOrUserNo, '').then((orders) => {
			const notLocked = orders?.filter(x => !x.IsLocked);
			const orderIds = notLocked ?? [];
	
			this.appState.setOrderIds(orderIds);
			
			return true;
		});

		return true;
	}

	public async createNewOrder(customer: CustomerModel | null, customerOrUserNo: string | undefined, crmTicketId?: string): Promise<number> {
		const canOnlyTestOrder = await this.userService.UserCanOnlyPlaceTestorder(customer != null);
		const isNewPartner = await this.onBehalfService.getIsNewPartner()

		const orderCreateModel = customer
			? OrderCreateModel.OrderOnBehalfCreateModel(customer.No, null)
			: new OrderCreateModel(canOnlyTestOrder);

		orderCreateModel.CrmTicketId = crmTicketId ?? this.appState.afterSalesCRMCaseNo.value;
		orderCreateModel.CrmFlowType = AppModeEnum[this.appState.appMode.value];

		const orderId = await this.orderDataService.createNewOrder(orderCreateModel, customerOrUserNo, false, isNewPartner);

		this.UpdateOrderIdsFromBackend();
		
		return orderId;
	}

	public async getIdOnCurrentOrder() {
		var orders: OrderIdsModel[] | undefined = [];
		orders = await this.onBehalfService.getCurrentActiveOrderIds();
		orders = orders?.filter(x => !x.IsLocked);

		if (orders && orders.length > 1) {
			throw new OrderItemAskForOrderIdError();
		}
		else if (orders?.length === 1) {
			return orders[0].Id;
		}
		else {
			return null;
		}
	}

	public async createOrderItem(orderItem: OrderItemModel) {
		const state = orderItem.getState();
		await this.orderDataService.createOrderItem(state, true);

		this.addToBagSubject.next(true);
	}

}
